import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PropTypes from 'prop-types';

import * as Styled from './PasswordValidationBox.style';

/**
 * Password validation requirements box
 * Shows real-time validation status for each password requirement
 *
 * @param {object} props - Component props
 * @param {string} props.value - Current password value
 * @returns {object} The PasswordValidationBox component
 */
function PasswordValidationBox({ value = '' }) {
  // Validation logic matching shopping-ui
  const hasMinLength = value.length >= 10;
  const hasMaxLength = value.length <= 128;
  const hasValidLength = hasMinLength && hasMaxLength;
  const hasUppercase = /[A-Z]/.test(value);
  const hasLowercase = /[a-z]/.test(value);
  const hasNumber = /[0-9]/.test(value);
  const hasSymbol = /[^\p{L}\d\s]/u.test(value);

  const validations = [
    { key: 'length', label: 'At least 10 and at most 128 characters', passed: hasValidLength },
    { key: 'uppercase', label: 'Upper case letters (A-Z)', passed: hasUppercase },
    { key: 'lowercase', label: 'Lower case letters (a-z)', passed: hasLowercase },
    { key: 'number', label: 'Numbers (0-9)', passed: hasNumber },
    { key: 'symbol', label: 'Symbols (!@#$%&*)', passed: hasSymbol }
  ];

  return (
    <Styled.Container aria-label="Password requirements" role="region">
      <Styled.Title size="xs" weight="medium">
        Your password must include:
      </Styled.Title>

      <Styled.ValidationList>
        {validations.map(({ key, label, passed }) => (
          <Styled.ValidationItem
            key={key}
            data-testid={`password-validation-${key}`}
            hasPassed={passed}
          >
            <Styled.IconWrapper hasPassed={passed}>
              {passed ? (
                <CheckCircleIcon fontSize="small" />
              ) : (
                <RadioButtonUncheckedIcon fontSize="small" />
              )}
            </Styled.IconWrapper>
            <Styled.ValidationText size="xs">{label}</Styled.ValidationText>
          </Styled.ValidationItem>
        ))}
      </Styled.ValidationList>
    </Styled.Container>
  );
}

PasswordValidationBox.propTypes = {
  error: PropTypes.object,
  value: PropTypes.string
};

export default PasswordValidationBox;
